'use client';

import React from 'react';
import { Link as CustomLink } from '@components/Link';
import CustomImage from '@components/CustomImage';
import { useLenis } from '@hooks/useLenis';
import cn from './style.module.scss';

interface DynamicButtonProps {
  children?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  scrollToId?: string;
  scrollOffset?: number;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  style?: React.CSSProperties;
  backgroundImage?: string;
  url?: string;
  linkLabel?: string;
  showLink?: boolean;
  target?: string;
  icon?: string | React.ReactNode;
  iconAlt?: string;
  iconPosition?: 'left' | 'right';
}

export default function DynamicButton({
  children,
  className = '',
  iconClassName = '',
  scrollToId,
  scrollOffset = 0,
  onClick,
  type = 'button',
  style,
  backgroundImage,
  url,
  linkLabel,
  showLink = false,
  target,
  icon,
  iconAlt = 'Icon',
  iconPosition = 'left',
}: DynamicButtonProps) {
  const { scrollTo } = useLenis();

  const handleClick = (
    e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    if (onClick) {
      onClick();
      return;
    }

    if (scrollToId) {
      e?.preventDefault();
      scrollTo(`#${scrollToId}`, { offset: scrollOffset });
      return;
    }

    if (url?.startsWith('#')) {
      e?.preventDefault();
      scrollTo(url, { offset: scrollOffset });
    }
  };

  const backgroundStyle = backgroundImage
    ? { backgroundImage: `url(${backgroundImage})` }
    : {};

  const iconElement = icon ? (
    <span className={`${cn.buttonIcon} ${iconClassName}`.trim()}>
      {typeof icon === 'string' ? (
        <CustomImage src={icon} alt={iconAlt} width={16} height={16} />
      ) : (
        icon
      )}
    </span>
  ) : null;

  const textElement = children || linkLabel;

  const renderContent = () => {
    if (iconPosition === 'right' && iconElement) {
      return (
        <>
          <span>{textElement}</span>
          {iconElement}
        </>
      );
    }

    return (
      <>
        {iconElement}
        <span>{textElement}</span>
      </>
    );
  };

  if (showLink && url?.startsWith('#')) {
    return (
      <a
        href={url}
        onClick={handleClick}
        className={`${cn.buttonElement} ${className}`.trim()}
        style={{ ...backgroundStyle, ...style }}
      >
        {renderContent()}
      </a>
    );
  }

  if (showLink && url) {
    return (
      <CustomLink
        className={`${cn.buttonElement} ${className}`.trim()}
        url={url}
        target={target}
      >
        {renderContent()}
      </CustomLink>
    );
  }

  return (
    <button
      type={type}
      onClick={handleClick}
      style={{ ...backgroundStyle, ...style }}
      className={`${cn.buttonElement} ${className}`.trim()}
    >
      {renderContent()}
    </button>
  );
}
